import { useState, useEffect } from 'react';
import { Trash, Image, Copy, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

export default function CMSMediaPanel({ token }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  const fetchFiles = async () => {
    try { const res = await api.get(`/admin/cms/media?token=${token}`); setFiles(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFiles(); }, []);

  const handleUpload = async (e) => {
    const fileList = Array.from(e.target.files);
    if (!fileList.length) return;
    setUploading(true);
    try {
      for (const file of fileList) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/admin/upload?token=${token}`, formData);
      }
      fetchFiles();
    } catch (e) { alert('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    try { await api.delete(`/admin/cms/media/${id}?token=${token}`); fetchFiles(); }
    catch (e) { alert('Failed to delete'); }
  };

  const copyUrl = (path) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;
    navigator.clipboard.writeText(url);
    setCopied(path);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  if (loading) return <div className="text-center py-12 text-[#4A5568]">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="cms-media-panel">
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-[#0A1F3D] mb-4">Upload Files</h3>
        <label className="cursor-pointer">
          <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
          <div className="border-2 border-dashed border-slate-300 hover:border-[#D4AF37] p-8 text-center transition-colors">
            <Image size={40} className="mx-auto text-slate-400 mb-3" />
            <p className="text-sm text-[#4A5568]">{uploading ? 'Uploading...' : 'Click to select images (or drag & drop)'}</p>
            <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP supported</p>
          </div>
        </label>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="p-4 border-b border-slate-200"><h3 className="font-bold text-[#0A1F3D]">{files.length} Files</h3></div>
        {files.length === 0 ? (
          <div className="text-center py-8 text-[#4A5568]">No files uploaded yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 p-4">
            {files.map((file) => (
              <div key={file.id} className="group relative border border-slate-200 overflow-hidden">
                <div className="aspect-square bg-slate-100">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${file.storage_path}`}
                    alt={file.original_filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button onClick={() => copyUrl(file.storage_path)} className="p-2 bg-white/20 hover:bg-white/40 rounded text-white" title="Copy URL">
                    {copied === file.storage_path ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                  <button onClick={() => handleDelete(file.id)} className="p-2 bg-red-500/50 hover:bg-red-500/80 rounded text-white" title="Delete">
                    <Trash size={16} />
                  </button>
                </div>
                <div className="p-2">
                  <p className="text-xs text-[#0A1F3D] truncate">{file.original_filename}</p>
                  <p className="text-xs text-[#4A5568]">{formatSize(file.size)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
